import Autocomplete, {
  AutocompleteRenderInputParams
} from '@mui/material/Autocomplete';
import ProfileField, { ProfileFieldType } from '../models/ProfileField';
import React, { ChangeEvent, useEffect, useState } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Unstable_Grid2';
import ProfileFieldInputVm from '../models/ProfileFieldInputVm';
import ProfileService from '../services/ProfileService';
import TextField from '@mui/material/TextField';
import { WILDCARD_FIELD_ITEM } from '../models/ProfileFieldItem';

interface UserProfileFieldControlProps {
  indicateErrorOnLoad: boolean;
  profileField: ProfileField;
  required: boolean;
  value: string;
  hidden?: boolean;
  onChange?: (input: ProfileFieldInputVm) => void;
  onSave?: (
    profilekey: number,
    fieldposition: number,
    userkey: number,
    value: string
  ) => void;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const UserProfileFieldControl = (props: UserProfileFieldControlProps) => {
  const [value, setValue] = useState<string>(props.value || '');
  const [invalidValue, setInvalidValue] = useState<boolean>(false);
  const profileField = props.profileField;
  const controlType = ProfileService.getControlType(props.profileField.type);
  const type = props.profileField.type;

  useEffect(() => {
    if (value !== props.value) {
      setValue(props?.value || '');
    }
  }, [props.value]);

  useEffect(() => {
    const isValueInvalid = () => {
      if (type === ProfileFieldType.TEXTLIST) {
        const parsedValue = ProfileService.parseMultiProfileValue(value);
        return (
          parsedValue.length &&
          !parsedValue.every((selectedValue) =>
            profileField.items?.some((option) => option.title === selectedValue)
          )
        );
      }
      if (type === ProfileFieldType.TEXT) {
        return (
          value && !profileField.items?.some((option) => option.title === value)
        );
      }
      return false;
    };

    setInvalidValue(isValueInvalid());
  }, [value, type]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (value !== e.target.value) {
      setNewValue(e.target.value);
    }
  };

  const handleComboBoxChange = (event: any, newValue: string | null) => {
    setNewValue(newValue);
    if (!newValue) {
      setInvalidValue(false); // Reset error when user clears the selected value.
    }
  };

  const handleMultiSelectChange = (event: any, newValue: string[]) => {
    if (newValue.length) {
      setNewValue(JSON.stringify(newValue));
    } else {
      setNewValue(undefined);
      setInvalidValue(false); // Reset error when user clears the selected value.
    }
  };

  function setNewValue(newValue: string) {
    setValue(newValue);
    const { profilekey, fieldposition, required } = props.profileField;
    props.onChange({
      profilekey,
      fieldposition,
      required,
      value: newValue
    });
  }
  /** Value indicating whether arbitrary text is allowed in dropdown. */
  const freeSolo = profileField?.items?.some(
    (item) => item.title === WILDCARD_FIELD_ITEM
  );

  const error = props.indicateErrorOnLoad
    ? props.required && (props.value == null || !value?.trim())
    : props.required && props.value !== undefined && !value?.trim(); //* The initial value of props.value is undefined.

  /** Get common props for `<AutoComplete>` */
  const commonProps = () => ({
    options: profileField.items
      ?.filter((item) => item.title !== WILDCARD_FIELD_ITEM)
      .map((item) => item.title)
      .filter(
        (title) =>
          !freeSolo ||
          (title || '')
            .toLocaleLowerCase()
            .includes((value || '').toLocaleLowerCase())
      ),
    fullWidth: true,
    freeSolo: freeSolo as undefined, // Cast as `undefined` for badly typed `freeSolo` prop.
    renderInput: (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        key={profileField.profilekey}
        label={profileField.title}
        variant="outlined"
        size="small"
        margin="dense"
        required={props.required}
        inputProps={{
          ...params.inputProps,
          required: error || invalidValue // Issue: B2-1734. This is required for the <form> default validation.
        }}
        error={error || invalidValue}
        onChange={freeSolo ? handleChange : undefined}
      />
    )
  });

  return (
    <Grid container>
      <Grid style={props.hidden ? { height: 0 } : { flex: 1 }}>
        {profileField.items?.length > 0 ? (
          type === ProfileFieldType.TEXTLIST ? (
            <Autocomplete
              {...commonProps()}
              multiple
              size="small"
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              value={ProfileService.parseMultiProfileValue(value)}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              onChange={handleMultiSelectChange}
            />
          ) : (
            <Autocomplete
              {...commonProps()}
              value={value || null}
              onChange={handleComboBoxChange}
            />
          )
        ) : type === ProfileFieldType.IMAGE ? (
          <>{/* Image fields currently only used for profile image. */}</>
        ) : (
          <TextField
            select={profileField.items?.length > 0}
            name={profileField.title}
            type={props.hidden ? 'hidden' : controlType}
            label={props.hidden ? '' : profileField.title}
            variant={props.hidden ? 'standard' : 'outlined'}
            fullWidth
            margin={props.hidden ? 'none' : 'dense'}
            size="small"
            value={value}
            required={props.required}
            error={error}
            InputLabelProps={
              type === ProfileFieldType.DATE ? { shrink: true } : {}
            }
            onChange={handleChange}
          ></TextField>
        )}
      </Grid>
    </Grid>
  );
};

export default UserProfileFieldControl;
